import styled from "styled-components";
import { createShouldForwardPropBlockList, deepMerge } from "../../utils/helper";
import { type LayoutProps } from "./layout";
import { getSidebarBackgroundColor, getSidebarBorder } from "./layout.styles.utils";

type SidebarProps = Pick<LayoutProps, "expanded"> & {
    layoutTheme: LayoutProps["theme"];
};
type SidebarToggleProps = Pick<LayoutProps, "expanded">;
type SidebarToggleBorderProps = {
    layoutTheme: LayoutProps["theme"];
};
type SidebarToggleButtonInnerWrapperProps = Pick<LayoutProps, "expanded">;
type SidebarToggleButtonTopProps = Pick<LayoutProps, "expanded">;
type SidebarToggleButtonBottomProps = Pick<LayoutProps, "expanded">;

export const Wrapper = styled("div")(({ theme }) => ({
    position: "absolute",
    inset: 0,
    display: "flex",
    width: "100%",
    height: "100%",
    flexDirection: "column",
    [`@media (min-width: ${theme.screens.lg})`]: {
        flexDirection: "row",
    },
}));

export const Sidebar = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["expanded", "layoutTheme"]),
})<SidebarProps>(({ expanded, layoutTheme, theme }) => ({
    position: "relative",
    width: "100%",
    flexShrink: 0,
    order: 2,
    ...deepMerge(getSidebarBackgroundColor(layoutTheme, theme)),
    height: `calc(${theme.spacing[12]} + env(safe-area-inset-bottom))`,
    [`@media (min-width: ${theme.screens.lg})`]: {
        display: "flex",
        flexDirection: "column",
        order: 1,
        width: expanded ? theme.spacing[64] : theme.spacing[16],
        height: "100%",
    },
}));

export const SidebarTop = styled("div")(({ theme }) => ({
    display: "none",
    height: theme.spacing[16],
    flexShrink: 0,
    paddingLeft: theme.spacing[4],
    paddingRight: theme.spacing[4],
    alignItems: "center",
    [`@media (min-width: ${theme.screens.lg})`]: {
        display: "flex",
    },
}));

export const SidebarMain = styled("div")(({ theme }) => ({
    height: theme.spacing[12],
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "center",
    scrollBehavior: "smooth",
    [`@media (min-width: ${theme.screens.lg})`]: {
        height: "100%",
        overflowY: "auto",
        display: "block",
    },
}));

export const SidebarInner = styled("div")(({ theme }) => ({
    paddingLeft: theme.spacing[4],
    paddingRight: theme.spacing[4],
}));

export const SidebarToggle = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["expanded"]),
})<SidebarToggleProps>(({ expanded, theme }) => ({
    display: "none",
    [`@media (min-width: ${theme.screens.lg})`]: {
        display: "flex",
        alignItems: "center",
        position: "absolute",
        left: "100%",
        top: 0,
        width: "40px",
        height: "100%",
        pointerEvents: "none",
        transform: "translateX(-1px)",
        zIndex: 10,
        "&:hover": {
            [`${SidebarToggleBorder}`]: {
                transform: expanded ? "translateX(-8px)" : "translateX(8px)",
            },
        },
    },
}));

export const SidebarToggleBorder = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["layoutTheme"]),
})<SidebarToggleBorderProps>(({ layoutTheme, theme }) => ({
    position: "absolute",
    left: 0,
    top: 0,
    width: "1px",
    height: "100%",
    transitionProperty: "transform",
    transitionDuration: "0.1s",
    transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
    ...deepMerge(getSidebarBorder(layoutTheme, theme)),
}));

export const SidebarToggleButton = styled("a")(({ theme }) => ({
    borderRadius: "6px",
    cursor: "pointer",
    display: "block",
    fill: theme.colors.gray[400],
    height: "56px",
    isolation: "isolate",
    position: "relative",
    right: "8px",
    width: "40px",
    zIndex: 1,
    pointerEvents: "auto",
}));

export const SidebarToggleButtonInnerWrapper = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["expanded"]),
})<SidebarToggleButtonInnerWrapperProps>(({ expanded, theme }) => ({
    borderRadius: "6px",
    cursor: "pointer",
    display: "block",
    height: "24px",
    left: "8px",
    position: "absolute",
    top: "16px",
    width: "16px",
    transitionProperty: "transform",
    transitionDuration: "0.1s",
    transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
    [`${SidebarToggleButton}:hover &`]: {
        transform: expanded ? "translateX(-4px)" : "translateX(12px)",
    },
}));

export const SidebarToggleButtonTop = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["expanded"]),
})<SidebarToggleButtonTopProps>(({ expanded, theme }) => ({
    position: "absolute",
    top: "5px",
    left: "7px",
    width: "2px",
    height: "8px",
    backgroundColor: theme.colors.gray[300],
    borderRadius: "2px",
    transitionProperty: "transform, background-color, height",
    transitionDuration: "0.1s",
    transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
    [`${SidebarToggleButton}:hover &`]: {
        backgroundColor: theme.colors.gray[500],
        height: "9px",
        transform: expanded ? "rotate(45deg)" : "rotate(-45deg)",
    },
}));

export const SidebarToggleButtonBottom = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["expanded"]),
})<SidebarToggleButtonBottomProps>(({ expanded, theme }) => ({
    position: "absolute",
    bottom: "5px",
    left: "7px",
    width: "2px",
    height: "8px",
    backgroundColor: theme.colors.gray[300],
    borderRadius: "2px",
    transitionProperty: "transform, background-color, height",
    transitionDuration: "0.1s",
    transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
    [`${SidebarToggleButton}:hover &`]: {
        backgroundColor: theme.colors.gray[500],
        height: "9px",
        transform: expanded ? "rotate(-45deg)" : "rotate(45deg)",
    },
}));

export const Body = styled("div")(({ theme }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    order: 1,
    height: `calc(100% - ${theme.spacing[12]} + env(safe-area-inset-bottom))`,
    [`@media (min-width: ${theme.screens.lg})`]: {
        order: 2,
        height: "100%",
    },
}));

export const Topbar = styled("div")(({ theme }) => ({
    height: theme.spacing[12],
    flexShrink: 0,
    boxShadow: theme.boxShadow["box-extra-light"],
    [`@media (min-width: ${theme.screens.lg})`]: {
        height: theme.spacing[16],
    },
}));

export const TopbarInner = styled("div")(({ theme }) => ({
    height: theme.spacing[12],
    marginLeft: theme.spacing[5],
    marginRight: theme.spacing[5],
    display: "flex",
    alignItems: "center",
    [`@media (min-width: ${theme.screens.lg})`]: {
        height: theme.spacing[16],
        marginLeft: theme.spacing[8],
        marginRight: theme.spacing[8],
    },
    [`@media (min-width: ${theme.screens.xl})`]: {
        marginLeft: theme.spacing[10],
        marginRight: theme.spacing[10],
    },
}));

export const Main = styled("div")(() => ({
    width: "100%",
    height: "100%",
    overflowY: "auto",
    scrollBehavior: "smooth",
}));

export const MainInner = styled("div")(({ theme }) => ({
    height: "100%",
    paddingLeft: theme.spacing[5],
    paddingRight: theme.spacing[5],
    [`@media (min-width: ${theme.screens.lg})`]: {
        paddingLeft: theme.spacing[8],
        paddingRight: theme.spacing[8],
    },
    [`@media (min-width: ${theme.screens.xl})`]: {
        paddingLeft: theme.spacing[10],
        paddingRight: theme.spacing[10],
    },
}));
